import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from '@/store'
import { debounce } from '@/utils'

import { selectDepotMapStyles, setCustomMapStyle } from '@/features/domain/ui'

export const useVisibility = (
  id: string,
): [boolean, React.MouseEventHandler<HTMLElement>, boolean] => {
  const dispatch = useAppDispatch()
  const mapStyles = useSelector(selectDepotMapStyles)
  const [loading, setLoading] = useState(false)
  const loadingRef = useRef(false)

  const globalStyle = mapStyles.mode
  const customStyle = mapStyles.custom[id]
  const mode = customStyle || globalStyle

  // Create the debounced function only when dependencies change
  const debouncedToggleVisibility = useMemo(
    () =>
      debounce(async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        if (loadingRef.current) return

        loadingRef.current = true
        setLoading(true)

        try {
          await dispatch(
            setCustomMapStyle({
              customStyle: { type: 'depot', mode: mode !== 'off' ? 'off' : 'on', ids: [id] },
            }),
          )
        } finally {
          loadingRef.current = false
          setLoading(false)
        }
      }, 200),
    [dispatch, id, mode],
  )

  // Callback that invokes the memoized debounced function
  const toggleVisibility = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      debouncedToggleVisibility(e)
    },
    [debouncedToggleVisibility],
  )

  return [mode !== 'off', toggleVisibility, loading]
}
